import { useRef, useState, useEffect } from "react";
import { LiveAvatarSession } from "@heygen/liveavatar-web-sdk";

const HEYGEN_API_KEY = process.env.REACT_APP_LIVEAVATAR_API_KEY;
const LIVEAVATAR_VOICE_ID = process.env.REACT_APP_LIVEAVATAR_VOICE_ID;
const LIVEAVATAR_CONTEXT_ID = process.env.REACT_APP_LIVEAVATAR_CONTEXT_ID;
const LIVEAVATAR_AVATAR_ID = process.env.REACT_APP_LIVEAVATAR_AVATAR_ID;

export default function LiveAvatarCustomDemo() {
  const videoRef = useRef(null);
  const clientRef = useRef(null);

  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [connected, setConnected] = useState(false);
  const [isAvatarReady, setIsAvatarReady] = useState(false);
  const hasInitializedRef = useRef(false);


  // Create Session Token
  const createSessionToken = async () => {
    const res = await fetch("https://api.liveavatar.com/v1/sessions/token", {
      method: "POST",
      headers: {
        "X-API-KEY": HEYGEN_API_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        mode: "FULL",
        avatar_id: LIVEAVATAR_AVATAR_ID,
        avatar_persona: {
          context_id: LIVEAVATAR_CONTEXT_ID,
          voice_id: LIVEAVATAR_VOICE_ID,
          language: "en",
        },
      }),
    });

    const data = await res.json();
    return data.data.session_token;
  };

  // Add Event Listeners
  const addEventListeners = (client) => {
    client.on("connected", () => {
      console.log("Transport connected");
    });

    client.on("avatar_start_talking", () => {
      setStatus("Avatar Speaking...");
    });

    client.on("avatar_stop_talking", () => {
      setStatus("Avatar Idle");

      // FIRST time the avatar finishes speaking = ready
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        setIsAvatarReady(true);
        console.log("Avatar is now READY for interaction");
      }
    });

    client.on("disconnected", () => {
      console.warn("Avatar disconnected from room");
    });

    client.on("error", (err) => {
      console.error("Avatar error:", err);
    });
  };

  // Start Session
  const startAvatar = async () => {
    try {
      setStatus("Creating session...");
      const token = await createSessionToken();

      const client = new LiveAvatarSession(token);
      clientRef.current = client;

      addEventListeners(client);

      setStatus("Starting avatar...");
      await client.start();

      // attach AFTER start (important)
      client.attach(videoRef.current);

    } catch (err) {
      console.error(err);
      setStatus("Failed to start avatar");
    }
  };

  // Stop Session
  const stopAvatar = async () => {
    if (!clientRef.current) return;

    try {
      setStatus("Stopping...");
      await clientRef.current.stop();
      clientRef.current = null;
      setConnected(false);
      setStatus("Stopped");
    } catch (err) {
      console.error("Stop error:", err);
    }
  };

  // Send Text → Lip Sync Speech
  const sendMessageToAvatar = async () => {
    if (!clientRef.current) return;
    if (!isAvatarReady) {
      console.warn("Avatar not ready yet");
      return;
    }
    if (!message.trim()) return;

    try {
      await clientRef.current.interrupt(); // safety
      await clientRef.current.repeat(message);
      setMessage("");
    } catch (err) {
      console.error("Speak error:", err);
    }
  };


  console.log("isAvatarReady", isAvatarReady);

  // Cleanup
  useEffect(() => {
    return () => {
      if (clientRef.current) {
        clientRef.current.stop();
      }
    };
  }, []);

  return (
    <div style={{ padding: 20 }}>
      <button onClick={startAvatar}>Start Avatar</button>
      <button onClick={stopAvatar} style={{ marginLeft: 10 }}>
        Stop
      </button>

      <div style={{ marginTop: 12 }}>
        <strong>Status:</strong> {status}
      </div>

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={false}
        style={{
          width: 420,
          height: 350,
          background: "#000",
          borderRadius: 12,
          marginTop: 20,
        }}
      />

      <div style={{ marginTop: 20, display: "flex", gap: 8 }}>
        <input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Say something..."
        />
        <button
          onClick={sendMessageToAvatar}
          disabled={!isAvatarReady}
        >
          Send
        </button>
      </div>
    </div>
  );
}
